import React, {
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Network from './Requests';
import {
  applyOptimizationPreset,
  createOptimizationPreset,
  deleteOptimizationPreset,
  downloadOptimizationPresetCatalog,
  mergeOptimizationPresetCatalogs,
  parseOptimizationPresetCatalog,
  readOptimizationPresetCatalog,
  replaceOptimizationPreset,
  writeOptimizationPresetCatalog,
} from './optimization-presets';

const fieldsetSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  m: 0,
  mb: 2.5,
  minWidth: 0,
  p: 2,
};

const buttonRowSx = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
};

const OptimizationPresetControls = ({
  active,
  disabled,
  onChange,
  profile,
  value,
}) => {
  const { t } = useTranslation();
  const componentId = useId();
  const fileInputRef = useRef(null);
  const activeSessionRef = useRef(0);
  const [catalog, setCatalog] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [userGuid, setUserGuid] = useState('');
  const selectId = `${componentId}-preset`;
  const selectLabelId = `${selectId}-label`;
  const nameId = `${componentId}-name`;
  const fileInputId = `${componentId}-import`;
  const controlsDisabled = (
    disabled ||
    !active ||
    isLoading ||
    catalog === null ||
    userGuid.length === 0
  );
  const normalizedName = name.trim();
  const presets = catalog?.presets ?? [];

  useEffect(() => {
    const activeSession = activeSessionRef.current + 1;
    let isCancelled = false;

    activeSessionRef.current = activeSession;
    setCatalog(null);
    setFeedback(null);
    setName('');
    setSelectedName('');
    setUserGuid('');

    if (!active) {
      setIsLoading(false);

      return () => {
        isCancelled = true;

        if (activeSessionRef.current === activeSession) {
          activeSessionRef.current += 1;
        }
      };
    }

    setIsLoading(true);

    const loadCatalog = async () => {
      try {
        const resolvedUserGuid = await new Network().GetUserGuid();

        if (
          isCancelled ||
          activeSessionRef.current !== activeSession
        ) {
          return;
        }

        if (
          typeof resolvedUserGuid !== 'string' ||
          resolvedUserGuid.trim().length === 0
        ) {
          setFeedback({
            key: 'optimizationPresets.messages.userUnavailable',
            severity: 'error',
          });
          return;
        }

        const normalizedUserGuid = resolvedUserGuid.trim();
        const result = readOptimizationPresetCatalog({
          storage: window.localStorage,
          userGuid: normalizedUserGuid,
        });

        if (
          isCancelled ||
          activeSessionRef.current !== activeSession
        ) {
          return;
        }

        setCatalog(result.catalog);
        setUserGuid(normalizedUserGuid);

        if (result.isCorrupted) {
          setFeedback({
            key: 'optimizationPresets.messages.corrupted',
            severity: 'warning',
          });
        }
      } catch (_error) {
        if (
          !isCancelled &&
          activeSessionRef.current === activeSession
        ) {
          setFeedback({
            key: 'optimizationPresets.messages.loadFailed',
            severity: 'error',
          });
        }
      } finally {
        if (
          !isCancelled &&
          activeSessionRef.current === activeSession
        ) {
          setIsLoading(false);
        }
      }
    };

    loadCatalog();

    return () => {
      isCancelled = true;

      if (activeSessionRef.current === activeSession) {
        activeSessionRef.current += 1;
      }
    };
  }, [active]);

  const setMessage = (key, severity = 'success', replacements) => {
    setFeedback({ key, replacements, severity });
  };

  const persistCatalog = (nextCatalog, successMessageKey) => {
    try {
      const writtenCatalog = writeOptimizationPresetCatalog({
        catalog: nextCatalog,
        storage: window.localStorage,
        userGuid,
      });

      setCatalog(writtenCatalog);
      setMessage(successMessageKey);

      return writtenCatalog;
    } catch (_error) {
      setMessage('optimizationPresets.messages.writeFailed', 'error');

      return null;
    }
  };

  const handlePresetChange = (nextName) => {
    if (nextName.length === 0) {
      setSelectedName('');
      return;
    }

    const preset = presets.find(({ name: presetName }) => (
      presetName === nextName
    ));

    try {
      const nextValue = applyOptimizationPreset({
        preset,
        profile,
        values: value,
      });

      setSelectedName(nextName);
      setFeedback(null);
      onChange(nextValue);
    } catch (_error) {
      setMessage('optimizationPresets.messages.loadFailed', 'error');
    }
  };

  const handleCreate = () => {
    if (normalizedName.length === 0) {
      setMessage('optimizationPresets.messages.invalidName', 'error');
      return;
    }

    const hasDuplicate = presets.some(({ name: presetName }) => (
      presetName.toLowerCase() === normalizedName.toLowerCase()
    ));

    if (hasDuplicate) {
      setMessage('optimizationPresets.messages.duplicate', 'error');
      return;
    }

    try {
      const nextCatalog = createOptimizationPreset({
        catalog,
        name: normalizedName,
        profile,
        values: value,
      });
      const writtenCatalog = persistCatalog(
        nextCatalog,
        'optimizationPresets.messages.saved',
      );

      if (writtenCatalog === null) {
        return;
      }

      const createdPreset = writtenCatalog.presets.find(({ name: presetName }) => (
        presetName.toLowerCase() === normalizedName.toLowerCase()
      ));

      setName('');
      setSelectedName(createdPreset?.name ?? normalizedName);
    } catch (error) {
      if (error?.code === 'duplicate-name') {
        setMessage('optimizationPresets.messages.duplicate', 'error');
      } else if (error?.code === 'invalid-name') {
        setMessage('optimizationPresets.messages.invalidName', 'error');
      } else {
        setMessage('optimizationPresets.messages.writeFailed', 'error');
      }
    }
  };

  const handleReplace = () => {
    try {
      const nextCatalog = replaceOptimizationPreset({
        catalog,
        name: selectedName,
        profile,
        values: value,
      });

      persistCatalog(
        nextCatalog,
        'optimizationPresets.messages.replaced',
      );
    } catch (_error) {
      setMessage('optimizationPresets.messages.writeFailed', 'error');
    }
  };

  const handleDelete = () => {
    if (!window.confirm(t('optimizationPresets.confirm.delete', {
      name: selectedName,
    }))) {
      return;
    }

    try {
      const nextCatalog = deleteOptimizationPreset({
        catalog,
        name: selectedName,
      });
      const writtenCatalog = persistCatalog(
        nextCatalog,
        'optimizationPresets.messages.deleted',
      );

      if (writtenCatalog !== null) {
        setSelectedName('');
      }
    } catch (_error) {
      setMessage('optimizationPresets.messages.writeFailed', 'error');
    }
  };

  const handleImport = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const activeSession = activeSessionRef.current;
    setIsLoading(true);

    try {
      const text = await file.text();

      if (activeSessionRef.current !== activeSession) {
        return;
      }

      const importedCatalog = parseOptimizationPresetCatalog(text);
      const mergeResult = mergeOptimizationPresetCatalogs({
        currentCatalog: catalog,
        importedCatalog,
      });

      if (
        mergeResult.conflictNames.length > 0 &&
        !window.confirm(t('optimizationPresets.confirm.importConflicts', {
          names: mergeResult.conflictNames.join(', '),
        }))
      ) {
        return;
      }

      if (activeSessionRef.current !== activeSession) {
        return;
      }

      const writtenCatalog = persistCatalog(
        mergeResult.catalog,
        'optimizationPresets.messages.imported',
      );

      if (writtenCatalog !== null && selectedName.length > 0) {
        const selectedPreset = writtenCatalog.presets.find(({ name: presetName }) => (
          presetName.toLowerCase() === selectedName.toLowerCase()
        ));

        setSelectedName(selectedPreset?.name ?? '');
      }
    } catch (_error) {
      if (activeSessionRef.current === activeSession) {
        setMessage('optimizationPresets.messages.invalidImport', 'error');
      }
    } finally {
      input.value = '';

      if (activeSessionRef.current === activeSession) {
        setIsLoading(false);
      }
    }
  };

  const handleExport = () => {
    try {
      downloadOptimizationPresetCatalog({ catalog });
      setFeedback(null);
    } catch (_error) {
      setMessage('optimizationPresets.messages.loadFailed', 'error');
    }
  };

  return (
    <Box component="fieldset" sx={fieldsetSx}>
      <Typography
        component="legend"
        sx={{ color: 'text.primary', fontWeight: 600, px: 0.75 }}
        variant="subtitle2"
      >
        {t('optimizationPresets.title')}
      </Typography>

      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <FormControl disabled={controlsDisabled} fullWidth size="small">
          <InputLabel id={selectLabelId}>
            {t('optimizationPresets.labels.preset')}
          </InputLabel>
          <Select
            disabled={controlsDisabled}
            id={selectId}
            label={t('optimizationPresets.labels.preset')}
            labelId={selectLabelId}
            onChange={(event) => handlePresetChange(event.target.value)}
            value={selectedName}
          >
            <MenuItem value="">
              {t('optimizationPresets.labels.none')}
            </MenuItem>
            {presets.map(({ name: presetName }) => (
              <MenuItem key={presetName} value={presetName}>
                {presetName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          disabled={controlsDisabled}
          fullWidth
          id={nameId}
          label={t('optimizationPresets.labels.name')}
          onChange={(event) => setName(event.target.value)}
          size="small"
          value={name}
        />

        <Box sx={buttonRowSx}>
          <Button
            disabled={controlsDisabled}
            onClick={handleCreate}
            size="small"
            variant="outlined"
          >
            {t('optimizationPresets.buttons.save')}
          </Button>
          <Button
            disabled={controlsDisabled || selectedName.length === 0}
            onClick={handleReplace}
            size="small"
            variant="outlined"
          >
            {t('optimizationPresets.buttons.replace')}
          </Button>
          <Button
            color="error"
            disabled={controlsDisabled || selectedName.length === 0}
            onClick={handleDelete}
            size="small"
            variant="outlined"
          >
            {t('optimizationPresets.buttons.delete')}
          </Button>
          <Button
            disabled={controlsDisabled}
            onClick={() => fileInputRef.current?.click()}
            size="small"
            variant="outlined"
          >
            {t('optimizationPresets.buttons.import')}
          </Button>
          <Button
            disabled={controlsDisabled}
            onClick={handleExport}
            size="small"
            variant="outlined"
          >
            {t('optimizationPresets.buttons.export')}
          </Button>
        </Box>

        <input
          accept=".json,application/json"
          aria-label={t('optimizationPresets.buttons.import')}
          disabled={controlsDisabled}
          hidden
          id={fileInputId}
          onChange={handleImport}
          ref={fileInputRef}
          type="file"
        />

        {feedback && (
          <Alert severity={feedback.severity}>
            {t(feedback.key, feedback.replacements)}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default OptimizationPresetControls;
